/// <amd-module name="Artifacts/Root/protocols/components/CopyableTextField" />
import * as Res from "Artifacts/Root/Resources";
import * as React from "react";
import { announce } from "VSS/Core/Util/Accessibility";
import { ClipboardButton, IClipboardButtonProps } from "VSSUI/Clipboard";
import { css } from "VSSUI/Util";

export interface ICopyableTextFieldProps {
  /**
   * Class name to apply to the outer element
   */
  className?: string;

  /**
   * Optional class name to apply to the inner 'content' element
   */
  contentClassName?: string;

  /**
   * Optional text to copy to the clipboard.
   * If no child components are supplied, this value will be used for the rendered content
   */
  text?: string;

  /**
   * Optional props to pass to the clipboard button.
   */
  buttonProps?: Omit<IClipboardButtonProps, "getContent">;

  /**
   * Optional text to scope copyable text for accessibility.
   * @default ""
   */
  ariaLabel?: string;
}

/**
 * Renders text (or child elements) with a 'copy' button. On click, the innerText
 * or supplied 'text' property value will be copied to the browser clipboard.
 *
 * If child components are supplied, they will be rendered, otherwise 'text' prop value will be used
 */
export const CopyableTextField: React.FunctionComponent<ICopyableTextFieldProps> = ({
  className,
  contentClassName,
  text,
  buttonProps,
  children,
}) => {
  const c = React.createRef<HTMLDivElement>();
  return (
    <div className={css(className, "copyable-text-field flex-row")}>
      <div className={css(contentClassName, "flex-grow copyable-field-content")} ref={c}>
        {children || text}
      </div>
      <ClipboardButton
        onCopy={() => announce(Res.Copied)}
        showCopiedTooltip={true}
        {...buttonProps}
        getContent={() => text || c.current!}
      />
    </div>
  );
};
